/**
 * First and last name on the user document (Josh, 11 Oct 2026; ships Sun 25).
 *
 * users/{uid}.firstName and users/{uid}.lastName sit beside `username`, which
 * stays the full name everything displays. They are written at sign-up (email
 * form, or Google's given_name/family_name) so a last-name sort can be exact
 * for new accounts.
 *
 * ⚠ INVARIANT: the two fields are either ABSENT or came from the same source
 * as the current `username`. A reader that finds them may trust them; absent
 * means fall back to the last-word rule on `username`. So anything that
 * replaces `username` must also replace or clear them: the account page rename
 * (one field until the redesign splits it) clears them, and a Google sign-in,
 * which rewrites `username` from Google every time, rewrites or clears them.
 */

const clean = v => (typeof v === 'string' ? v.trim() : '');

/** Email sign-up: the two form fields, trimmed, and the full name built from them. */
export function signupNameFields(first, last) {
    const firstName = clean(first);
    const lastName = clean(last);
    return { username: [firstName, lastName].filter(Boolean).join(' '), firstName, lastName };
}

/** Google sign-in: res.additionalUserInfo.profile. Both parts or nothing. */
export function googleNameFields(profile) {
    if (!profile || typeof profile !== 'object') return null;
    const firstName = clean(profile.given_name);
    const lastName = clean(profile.family_name);
    return firstName && lastName ? { firstName, lastName } : null;
}

/**
 * Account page rename. `deleteValue` is firebase.firestore.FieldValue.delete(),
 * passed in so this file needs no Firebase import. Saving the same name (ignoring
 * surrounding spaces) is not a rename and keeps the stored parts.
 */
export function renameUpdate(newName, currentName, deleteValue) {
    const update = { username: newName };
    if (clean(newName) !== clean(currentName)) {
        update.firstName = deleteValue;
        update.lastName = deleteValue;
    }
    return update;
}

/**
 * Split a Firestore update into what to copy onto the local user object and
 * which keys to remove, so a FieldValue.delete() sentinel never sits in local state.
 */
export function localUserPatch(update, isDelete) {
    const assign = {};
    const remove = [];
    Object.entries(update || {}).forEach(([k, v]) => {
        if (isDelete(v)) remove.push(k);
        else assign[k] = v;
    });
    return { assign, remove };
}


// WEBPACK FOOTER //
// ./src/lib/user-name.js